'use client';

import type { ReactNode } from 'react';

/** The same navy gradient band the locations sheet uses for its header. */
export const SHEET_HEADER_GRADIENT =
  'linear-gradient(135deg, #1c2b39 0%, #243848 50%, #2d4a5c 100%)';

interface SheetHeaderProps {
  title: ReactNode;
  /** Small line under the title */
  subtitle?: ReactNode;
  /** Something above the title, e.g. a status badge */
  eyebrow?: ReactNode;
  /** An icon shown in a white disc beside the title */
  icon?: ReactNode;
  className?: string;
}

/** The teal glow in the corner of every sheet header, so the navy band is not flat. */
export function HeaderGlow() {
  return (
    <>
      <span
        aria-hidden
        className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full opacity-60 blur-3xl"
        style={{ background: 'radial-gradient(closest-side, #299c8f, transparent)' }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute -bottom-28 -left-10 h-48 w-48 rounded-full opacity-30 blur-3xl"
        style={{ background: 'radial-gradient(closest-side, #4cb8ab, transparent)' }}
      />
    </>
  );
}

export function SheetHeader({ title, subtitle, eyebrow, icon, className = '' }: SheetHeaderProps) {
  return (
    <div
      className={`relative overflow-hidden px-6 pt-8 pb-6 text-white sm:px-8 sm:pt-9 ${className}`}
      style={{ background: SHEET_HEADER_GRADIENT }}
    >
      <HeaderGlow />
      <div className="relative z-10 flex items-start gap-4">
        {icon && (
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white shadow-sm">
            {icon}
          </span>
        )}
        <div className="min-w-0 flex-1">
          {eyebrow && <div className="mb-2">{eyebrow}</div>}
          <h2 className="font-display text-4xl leading-none font-normal tracking-wide uppercase sm:text-5xl">
            {title}
          </h2>
          {subtitle && <p className="mt-2 text-sm font-medium text-white/75">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

/**
 * A person's sheet header: the navy band with their photo, which card the
 * sheet is for as a small kicker, their name big and a line under it.
 */
export function PersonSheetHeader({
  photo,
  kicker,
  name,
  sub,
}: {
  /** The face, usually tappable to change it */
  photo: ReactNode;
  kicker: ReactNode;
  name: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <div
      className="relative overflow-hidden px-6 pt-8 pb-6 text-white sm:px-8 sm:pt-9"
      style={{ background: SHEET_HEADER_GRADIENT }}
    >
      <HeaderGlow />
      <div className="relative z-10 flex items-center gap-4">
        {photo}
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold tracking-widest text-white/60 uppercase">{kicker}</p>
          <h2 className="pii font-display mt-0.5 text-4xl leading-none tracking-wide uppercase sm:text-5xl">
            {name}
          </h2>
          {sub && <p className="pii mt-1.5 text-sm font-medium text-white/75">{sub}</p>}
        </div>
      </div>
    </div>
  );
}
